export const createIdForLeader = (leader) =>
  `${leader.memberName}-${leader.positionId}-${leader.divisionId}`;

const createParentInfo = () => ({
  positionId: null,
  positionName: null,
  memberId: null,
  memberName: null,
  divisionId: null,
  divisionName: null,
  subDivisionId: null,
  subDivisionName: null,
});

const createNewChild = (childName) => ({
  structureName: childName,
  createdBy: createParentInfo(),
  updatedBy: createParentInfo(),
  createdDt: new Date().toISOString(),
  updatedDt: new Date().toISOString(),
  totalMembersCount: null,
  leaders: [],
});

const createLeaderNode = (formValues) => ({
  positionId: formValues.positionId,
  positionName: formValues.positionName,
  memberId: null,
  memberName: formValues.memberName,
  divisionId: formValues.divisionId,
  divisionName: formValues.divisionName,
  subDivisionId: formValues?.subDivisionId,
  subDivisionName: formValues?.subDivisionName,
  isNew: true,
});

export const addNewStrutureNode = (
  currentProposedData,
  name,
  selectedStructureName
) => {
  console.log({ name, selectedStructureName });
  const { parent } = currentProposedData;
  const isChildCreation = parent.structureName === selectedStructureName;

  // child
  if (isChildCreation) {
    const newChild = {
      child: createNewChild(name),
      subChildren: [],
      isNew: true,
    };
    const newChildrens = [...currentProposedData.children, newChild];

    return {
      ...currentProposedData,
      children: newChildrens,
    };
  }

  // sub-child
  const { children } = currentProposedData;
  const selectedChildIndex = children.findIndex(
    (c) => !c.isRemoved && c.child.structureName === selectedStructureName
  );

  const { subChildren } = children[selectedChildIndex];
  const newSubChildren = [
    ...subChildren,
    { ...createNewChild(name), isNew: true },
  ];
  const newChildrens = children.with(selectedChildIndex, {
    ...children[selectedChildIndex],
    subChildren: newSubChildren,
  });

  return {
    ...currentProposedData,
    children: newChildrens,
  };
};

export const editStrutureNode = (
  currentProposedData,
  updateFields,
  selectedStructureName
) => {
  // parent
  const { parent } = currentProposedData;
  const isParentNeedsUpdate = parent.structureName === selectedStructureName;

  if (isParentNeedsUpdate) {
    const newParent = {
      ...parent,
      ...updateFields,
    };

    return {
      ...currentProposedData,
      parent: newParent,
    };
  }

  // child
  const { children } = currentProposedData;
  const selectedChildIndex = children.findIndex(
    (c) => c.child.structureName === selectedStructureName
  );

  if (selectedChildIndex > -1) {
    const { child } = children[selectedChildIndex];
    const newChild = { ...child, ...updateFields };
    const newChildrens = children.with(selectedChildIndex, {
      ...children[selectedChildIndex],
      child: newChild,
    });

    return {
      ...currentProposedData,
      children: newChildrens,
    };
  }

  // check in subChildren
  const newChildren = children.reduce((accu, current) => {
    const { subChildren } = current;
    const selectedSubChildLeaderIndex = subChildren.findIndex(
      (subChild) => subChild.structureName === selectedStructureName
    );

    if (selectedSubChildLeaderIndex > -1) {
      const selectedSubChild = subChildren[selectedSubChildLeaderIndex];
      const newSubChildrens = subChildren.with(selectedSubChildLeaderIndex, {
        ...selectedSubChild,
        ...updateFields,
      });
      return [
        ...accu,
        {
          ...current,
          subChildren: newSubChildrens,
        },
      ];
    }
    return [...accu, current];
  }, []);

  return {
    ...currentProposedData,
    children: newChildren,
  };
};

export const removeStrutureByName = (currentProposedData, structureName) => {
  // check in children and subchildren
  const { children } = currentProposedData;
  const newChildren = children.reduce((acc, curr) => {
    const { child, subChildren } = curr;

    // check in child
    const isChildToggle = child.structureName === structureName;
    if (isChildToggle) {
      return acc;
    }

    // check in subChildren
    const newSubChildren = subChildren.filter(
      (sub) => sub.structureName !== structureName
    );

    return [...acc, { ...curr, subChildren: newSubChildren }];
  }, []);

  return {
    ...currentProposedData,
    children: newChildren,
  };
};

const isLeaderPresent = (leader, id) => createIdForLeader(leader) === id;

const getNewleaders = (leaders, updateFields, index) => {
  if (updateFields.isRemoved) {
    return leaders.filter((_, idx) => idx !== index);
  }
  const selectedLeader = leaders[index];

  return leaders.with(index, {
    ...selectedLeader,
    ...updateFields,
  });
};

export const updateLeaderByMemberId = (
  currentProposedData,
  id,
  updateFields
) => {
  // check in parent
  const { parent } = currentProposedData;
  const selectedParentLeaderIndex = parent.leaders.findIndex((leader) =>
    isLeaderPresent(leader, id)
  );

  if (selectedParentLeaderIndex > -1) {
    return {
      ...currentProposedData,
      parent: {
        ...currentProposedData.parent,
        leaders: getNewleaders(
          parent.leaders,
          updateFields,
          selectedParentLeaderIndex
        ),
      },
    };
  }

  // check in children and subchildren
  const { children } = currentProposedData;
  const newChildren = children.reduce((acc, curr) => {
    const { child, subChildren } = curr;

    // check in child
    const selectedChildIndex = child.leaders.findIndex((leader) =>
      isLeaderPresent(leader, id)
    );

    if (selectedChildIndex > -1) {
      return [
        ...acc,
        {
          ...curr,
          child: {
            ...child,
            leaders: getNewleaders(
              child.leaders,
              updateFields,
              selectedChildIndex
            ),
          },
        },
      ];
    }

    // check in subChildren
    const newSubChildren = subChildren.reduce((accu, current) => {
      const { leaders } = current;
      const selectedSubChildLeaderIndex = leaders.findIndex((leader) =>
        isLeaderPresent(leader, id)
      );

      if (selectedSubChildLeaderIndex > -1) {
        return [
          ...accu,
          {
            ...current,
            leaders: getNewleaders(
              leaders,
              updateFields,
              selectedSubChildLeaderIndex
            ),
          },
        ];
      }
      return [...accu, current];
    }, []);

    return [...acc, { child, subChildren: newSubChildren }];
  }, []);

  return {
    ...currentProposedData,
    children: newChildren,
  };
};

export const addNewLeaderNode = (
  currentProposedData,
  formValues,
  structureName
) => {
  const leaderNode = createLeaderNode(formValues);

  // Parent
  const { parent } = currentProposedData;
  const isParent = parent.structureName === structureName;
  if (isParent) {
    const newLeaders = [...parent.leaders, leaderNode];
    return {
      ...currentProposedData,
      parent: {
        ...parent,
        leaders: newLeaders,
      },
    };
  }

  const { children } = currentProposedData;
  const selectedChildrenIndex = children.findIndex(
    ({ child }) => child.structureName === structureName
  );

  // child
  if (selectedChildrenIndex > -1) {
    const { child } = children[selectedChildrenIndex];
    const newChild = { ...child, leaders: [...child.leaders, leaderNode] };
    const newChildren = children.with(selectedChildrenIndex, {
      ...children[selectedChildrenIndex],
      child: newChild,
    });

    return {
      ...currentProposedData,
      children: newChildren,
    };
  }

  // sub-child
  const newChildren = children.reduce((acc, curr) => {
    const { subChildren } = curr;
    const selectedSubChildrenIndex = subChildren.findIndex(
      (subChild) => subChild.structureName === structureName
    );

    if (selectedSubChildrenIndex > -1) {
      const selectedSubChildren = subChildren[selectedSubChildrenIndex];
      const newSubChildren = {
        ...selectedSubChildren,
        leaders: [...selectedSubChildren.leaders, leaderNode],
      };
      const newSubChildrens = subChildren.with(
        selectedSubChildrenIndex,
        newSubChildren
      );

      return [...acc, { ...curr, subChildren: newSubChildrens }];
    }

    return [...acc, curr];
  }, []);

  return {
    ...currentProposedData,
    children: newChildren,
  };
};

export const getLeaderByMemberId = (currentProposedData, id) => {
  const { parent, children } = currentProposedData;

  const allLeaders = children.reduce(
    (acc, curr) => {
      const { child, subChildren } = curr;
      const subChildLeaders = subChildren.map((sub) => sub.leaders).flat();

      return [...acc, ...child.leaders, ...subChildLeaders];
    },
    [...parent.leaders]
  );

  return allLeaders.find((leader) => createIdForLeader(leader) === id);
};

export const changeHierarchyInProposedChart = (
  currentProposedData,
  originId,
  destinationId
) => {
  // Remove subchild
  const allSubchildren = currentProposedData.children
    .map((child) => child.subChildren)
    .flat();
  const selectedSubChild = allSubchildren.find(
    (subChild) => subChild.structureName === originId
  );
  const destinationChildrenIndex = currentProposedData.children.findIndex(
    ({ child }) => child.structureName === destinationId
  );
  const { subChildren } =
    currentProposedData.children[destinationChildrenIndex];
  const isPresent = subChildren.some((sub) => sub.structureName === originId);

  if (!isPresent) {
    const proposedWithoutOrigin = removeStrutureByName(
      currentProposedData,
      originId
    );

    // Add subchild to it's destination children
    const { children } = proposedWithoutOrigin;
    if (destinationChildrenIndex > -1) {
      const { subChildren: proposedSubChildren } =
        children[destinationChildrenIndex];
      const newSubChildren = [
        ...proposedSubChildren,
        { ...selectedSubChild, isNew: true },
      ];

      const newChildrens = children.with(destinationChildrenIndex, {
        ...children[destinationChildrenIndex],
        subChildren: newSubChildren,
      });

      return {
        ...proposedWithoutOrigin,
        children: newChildrens,
      };
    }
  }

  return currentProposedData;
};
